/**
 * The work item page's six acts, on screen (ADR-0044, plan Task 8b).
 *
 * The backend half lives in `workItems/pageActs.ts` and `workItems/runAgain.ts`.
 * These are the controls, and they all obey one rule the backend obeys too:
 * **none of them decides the gate**. Answering a question the plan raised,
 * replying to a read, leaving a note for the next step, splitting the work and
 * sending it back to the first stage are all things a person can do while the
 * decision stays exactly where it was. The one exception is offering the plan
 * again after a stopped build (`usePageActs.reofferPlan`), which consumes the
 * stop gate on its way to re-opening the approval; it lives on the stop card
 * and the dock, not here.
 *
 * That rule is why they are quiet: the gate's own two verbs are the loud pair
 * on this page, and a second primary beside them would make a reader choose
 * between two things that are not alternatives.
 *
 * Copy rules: no em-dashes, never "job" for a ticket, never "Worker", never
 * "container", never "task" for a work item. The customer word is Ticket.
 */
import { useState } from "react";
import type { WorkItemCommentDto } from "@yagni/shared";

/* ── copy ──────────────────────────────────────────────────────────────────
   Kept here rather than in `workCopy.ts` because every string in this file
   belongs to one feature and is read next to the control that prints it. */

// The answer on the record, as the plan's question block prints it. The
// standalone "Send answer" verb that used to sit beside it is gone from the
// page: a staged answer rides the dock's own verbs (Approve, Send back), which
// is the one sender on the page.
export const ANSWERED_LABEL = "Answered";
export const ANSWER_SECOND_NOTE = "This was already answered. Yours is recorded beside the first.";

export const READ_REPLY_VERB = "Reply";
export const READ_REPLY_PLACEHOLDER = "What should the next pass know?";
export const READ_REPLY_EFFECT = "A reply is read on the next pass. It does not clear this read.";
export const READ_REPLIES_LABEL = "Replies";

export const SPLIT_VERB = "Split into tickets";
export const SPLIT_CANCEL = "Cancel";
export const SPLIT_CONFIRM = "Create these tickets";
export const SPLIT_ADD = "Add a ticket";
export const SPLIT_REMOVE = "Remove";
export const SPLIT_TITLE_PLACEHOLDER = "What this ticket covers";
export const SPLIT_STEPS_PLACEHOLDER = "One step per line";
export const SPLIT_EFFECT =
  "Each ticket goes to this Team on the same lane, supervised by the same person, and gets its own plan. This one closes as split.";
export const SPLIT_EMPTY = "Give at least one ticket a title.";

export const RETURN_VERB = "Back to Proposed";
export const RETURN_PLACEHOLDER = "Why it is going back";
export const RETURN_CONFIRM = "Send it back";
export const RETURN_CANCEL = "Cancel";
export const RETURN_EFFECT =
  "This goes back to the first stage with your note. No proposal is accepted, and nothing is planned.";
export const RETURN_EFFECT_SPLIT =
  "This goes back to the first stage with your note, and the tickets it was split into close. Nothing is planned.";

export const NEXT_STEP_LABEL = "For the next step";
export const NEXT_STEP_PLACEHOLDER = "What the next step should know";
export const NEXT_STEP_SAVE = "Leave the note";
export const NEXT_STEP_EFFECT =
  "The next step reads this before it starts. One note at a time; a new one replaces it.";
export const NEXT_STEP_CONSUMED = "Read at the last step boundary.";
export const NEXT_STEP_HINT = "Comments on this ticket reach the next pass the same way.";

export const RUN_AGAIN_VERB = "Run again";
export const RUN_AGAIN_EFFECT =
  "This starts the same line again. It is a new run, not this one resumed.";

/* ── one small form, used four times ───────────────────────────────────────── */

export function useSubmit(send: (text: string) => Promise<void>): {
  busy: boolean;
  error: string | null;
  submit: (text: string, onDone: () => void) => Promise<void>;
} {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (text: string, onDone: () => void) => {
    if (!text.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      await send(text.trim());
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : "That did not go through.");
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, submit };
}

/* ── a plan question's answers ─────────────────────────────────────────────── */

/** The answer a question already has, and where it came from. */
export interface QuestionAnswer {
  id: string;
  body: string;
  /** Who answered, as the record names them. */
  authorName: string | null;
  createdAt: string;
  /** True on every answer after the first: shown as a note beside it. */
  second: boolean;
  /**
   * Set when Reeve chose the answer because its plan line approved the plan
   * with no person at the gate: the plain line printed in place of a name.
   */
  reeveNote?: string | null;
}

/**
 * The plain line for an answer Reeve staged on an autonomous approval, by how
 * it was reached. Null for anything else, so a person's answer never borrows
 * Reeve's name.
 */
export function reeveAnswerNote(basis: unknown, answer: string, why: string): string | null {
  if (basis === "recommended") return "Reeve chose the recommended answer";
  if (basis === "assumed") {
    const assumed = answer.trim().replace(/^Assumed:\s*/i, "");
    return assumed ? `Reeve assumed: ${assumed}` : "Reeve assumed the most conservative reading";
  }
  if (basis !== "judgment") return null;
  const decided = answer.trim().replace(/\.+$/, "");
  const reason = why.trim();
  const said = reason ? ` ${reason.charAt(0).toUpperCase()}${reason.slice(1)}` : "";
  return `Reeve decided: ${decided}.${said}`;
}

/* ── 29. replying to a read ────────────────────────────────────────────────── */

/** The replies made against one read, oldest first. */
export function repliesForRead(
  comments: readonly WorkItemCommentDto[],
  runId: string | null,
): WorkItemCommentDto[] {
  if (!runId) return [];
  return comments
    .filter((comment) => comment.replyToRunId === runId)
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

export function ReadReplies({
  runId,
  replies,
  onReply,
}: {
  /** The run that made the read. Null hides the box: there is nothing to address. */
  runId: string | null;
  replies: readonly WorkItemCommentDto[];
  onReply: (runId: string, body: string) => Promise<void>;
}) {
  const [draft, setDraft] = useState("");
  const [open, setOpen] = useState(false);
  const { busy, error, submit } = useSubmit((text) =>
    runId ? onReply(runId, text) : Promise.resolve(),
  );
  if (!runId) return null;

  return (
    <div className="cf-read-replies" data-testid="cf-read-replies">
      {replies.length > 0 && (
        <div className="cf-read-replies-list">
          <span className="cf-read-replies-lab">{READ_REPLIES_LABEL}</span>
          {replies.map((reply) => (
            <p key={reply.id} className="cf-read-reply" data-testid="cf-read-reply">
              <span className="cf-read-reply-who">{reply.authorName ?? "Someone"}</span>
              <span className="cf-read-reply-body">{reply.body}</span>
            </p>
          ))}
        </div>
      )}
      {open ? (
        <div className="pq-own">
          <textarea
            className="wk-gate-input"
            rows={2}
            value={draft}
            placeholder={READ_REPLY_PLACEHOLDER}
            aria-label={READ_REPLY_PLACEHOLDER}
            data-testid="cf-read-reply-input"
            onChange={(e) => setDraft(e.target.value)}
          />
          <div className="pq-own-acts">
            <button
              type="button"
              className="deccard-go is-primary"
              disabled={!draft.trim() || busy}
              title={READ_REPLY_EFFECT}
              data-testid="cf-read-reply-send"
              onClick={() =>
                void submit(draft, () => {
                  setDraft("");
                  setOpen(false);
                })
              }
            >
              {READ_REPLY_VERB}
            </button>
            <span className="cf-read-reply-effect">{READ_REPLY_EFFECT}</span>
          </div>
          {error && (
            <p className="wk-gate-err" role="alert">
              {error}
            </p>
          )}
        </div>
      ) : (
        <button
          type="button"
          className="is-quiet"
          data-testid="cf-read-reply-open"
          title={READ_REPLY_EFFECT}
          onClick={() => setOpen(true)}
        >
          {READ_REPLY_VERB}
        </button>
      )}
    </div>
  );
}

/* ── 30. split, and back to proposed ───────────────────────────────────────── */

export interface SplitDraftRow {
  title: string;
  steps: string;
  /** The Team repository this ticket builds in (YAG-1058); "" for none. */
  repoId?: string;
}

/** A repository a split row may name: the Team's, as the case file reads them. */
export interface SplitRepo {
  id: string;
  fullName: string;
}

export const SPLIT_REPO_LABEL = "Builds in";
export const SPLIT_REPO_NONE = "Let the Team choose";
export const SPLIT_REPOS_ALL_OR_NONE =
  "Name a repository on every ticket to build them in order, or on none.";
export const SPLIT_ORDERED_EFFECT =
  "With a repository on every ticket they build in this order: the first plans now, each next one when the previous pull request merges.";

/**
 * The split panel, opened in place from the plan card's quiet verb.
 *
 * It opens PRE-FILLED from the plan's own steps, because the plan already said
 * what the pieces are and making a person retype them would be asking them to
 * do the plan's work again. Every row is still theirs to rewrite. On a Team
 * with several repositories each row may name the one it builds in; naming
 * one on every row makes the tickets an ordered chain (YAG-1058).
 */
export function SplitPanel({
  suggested,
  repos = [],
  onCancel,
  onSplit,
}: {
  /** One row per proposed ticket, from the plan's steps. */
  suggested: readonly SplitDraftRow[];
  /** The Team's repositories; a picker shows on each row when there are several. */
  repos?: readonly SplitRepo[];
  onCancel: () => void;
  /**
   * `idempotencyKey` is this panel's one key, the same on every try: a second
   * click, or a retry after a response that never came back, gets the first
   * split's tickets instead of "already split".
   */
  onSplit: (
    children: { title: string; steps: string[]; repoId?: string }[],
    idempotencyKey: string,
  ) => Promise<void>;
}) {
  const [rows, setRows] = useState<SplitDraftRow[]>(
    suggested.length > 0 ? [...suggested] : [{ title: "", steps: "" }],
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [idempotencyKey] = useState(() => crypto.randomUUID());
  const ready = rows.some((row) => row.title.trim());
  // The picker shows when the Team has repositories to choose between. A row
  // that came with a repository (the plan gate's split offer) keeps it even
  // when the Team's list could not be read: the order the plan found is the
  // point of the offer, and the server checks every repository against the
  // Team before it writes anything.
  const pickRepos = repos.length > 1;
  const named = rows.filter((row) => row.title.trim() && row.repoId).length;
  const ordered = named > 0;

  const setRow = (index: number, patch: Partial<SplitDraftRow>) => {
    setRows((current) => current.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const go = async () => {
    const kept = rows.filter((row) => row.title.trim());
    const children = kept.map((row) => ({
      title: row.title.trim(),
      steps: row.steps
        .split("\n")
        .map((step) => step.trim())
        .filter(Boolean),
      ...(row.repoId ? { repoId: row.repoId } : {}),
    }));
    if (children.length === 0) {
      setError(SPLIT_EMPTY);
      return;
    }
    if (ordered && named < kept.length) {
      setError(SPLIT_REPOS_ALL_OR_NONE);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await onSplit(children, idempotencyKey);
    } catch (err) {
      setError(err instanceof Error ? err.message : "That did not go through.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="cf-split" data-testid="cf-split-panel">
      <p className="cf-split-effect" data-testid="cf-split-effect">
        {ordered ? SPLIT_ORDERED_EFFECT : SPLIT_EFFECT}
      </p>
      {rows.map((row, index) => (
        <div key={index} className="cf-split-row" data-testid="cf-split-row">
          {pickRepos && (
            <select
              className="wk-gate-input cf-split-repo"
              value={row.repoId ?? ""}
              aria-label={SPLIT_REPO_LABEL}
              data-testid="cf-split-repo"
              onChange={(e) => setRow(index, { repoId: e.target.value })}
            >
              <option value="">{SPLIT_REPO_NONE}</option>
              {repos.map((repo) => (
                <option key={repo.id} value={repo.id}>
                  {repo.fullName}
                </option>
              ))}
            </select>
          )}
          <input
            className="wk-gate-input cf-split-title"
            value={row.title}
            placeholder={SPLIT_TITLE_PLACEHOLDER}
            aria-label={SPLIT_TITLE_PLACEHOLDER}
            data-testid="cf-split-title"
            onChange={(e) => setRow(index, { title: e.target.value })}
          />
          <textarea
            className="wk-gate-input cf-split-steps"
            rows={2}
            value={row.steps}
            placeholder={SPLIT_STEPS_PLACEHOLDER}
            aria-label={SPLIT_STEPS_PLACEHOLDER}
            data-testid="cf-split-steps"
            onChange={(e) => setRow(index, { steps: e.target.value })}
          />
          {rows.length > 1 && (
            <button
              type="button"
              className="is-quiet"
              data-testid="cf-split-remove"
              onClick={() => setRows((current) => current.filter((_, i) => i !== index))}
            >
              {SPLIT_REMOVE}
            </button>
          )}
        </div>
      ))}
      <div className="tri-actions cf-split-acts">
        <button
          type="button"
          className="is-quiet"
          data-testid="cf-split-add"
          onClick={() => setRows((current) => [...current, { title: "", steps: "" }])}
        >
          {SPLIT_ADD}
        </button>
        <button
          type="button"
          className="deccard-go is-primary"
          disabled={!ready || busy}
          title={SPLIT_EFFECT}
          data-testid="cf-split-confirm"
          onClick={() => void go()}
        >
          {SPLIT_CONFIRM}
        </button>
        <button type="button" className="is-quiet" onClick={onCancel} data-testid="cf-split-cancel">
          {SPLIT_CANCEL}
        </button>
      </div>
      {error && (
        <p className="wk-gate-err" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

export function ReturnToProposedPanel({
  onCancel,
  onReturn,
  split = false,
}: {
  onCancel: () => void;
  onReturn: (note: string) => Promise<void>;
  /** A split ticket: going back closes the tickets it was split into. */
  split?: boolean;
}) {
  const [note, setNote] = useState("");
  const { busy, error, submit } = useSubmit(onReturn);
  const effect = split ? RETURN_EFFECT_SPLIT : RETURN_EFFECT;
  return (
    <div className="cf-return" data-testid="cf-return-panel">
      <p className="cf-return-effect">{effect}</p>
      <textarea
        className="wk-gate-input"
        rows={2}
        value={note}
        placeholder={RETURN_PLACEHOLDER}
        aria-label={RETURN_PLACEHOLDER}
        data-testid="cf-return-note"
        onChange={(e) => setNote(e.target.value)}
      />
      <div className="tri-actions">
        <button
          type="button"
          className="deccard-go is-primary"
          disabled={!note.trim() || busy}
          title={effect}
          data-testid="cf-return-confirm"
          onClick={() => void submit(note, () => setNote(""))}
        >
          {RETURN_CONFIRM}
        </button>
        <button
          type="button"
          className="is-quiet"
          onClick={onCancel}
          data-testid="cf-return-cancel"
        >
          {RETURN_CANCEL}
        </button>
      </div>
      {error && (
        <p className="wk-gate-err" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

/* ── 32. the next-step note ────────────────────────────────────────────────── */

/** The note standing right now, and the last one that was read. */
export function nextStepNotes(comments: readonly WorkItemCommentDto[]): {
  standing: WorkItemCommentDto | null;
  consumed: WorkItemCommentDto[];
} {
  const flagged = comments
    .filter((comment) => comment.forNextStep)
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  const standing = [...flagged].reverse().find((comment) => !comment.consumedAt) ?? null;
  return {
    standing,
    consumed: flagged.filter((comment) => comment.consumedAt),
  };
}

export function NextStepNote({
  standing,
  consumed,
  onLeaveNote,
}: {
  standing: WorkItemCommentDto | null;
  consumed: readonly WorkItemCommentDto[];
  onLeaveNote: (body: string) => Promise<void>;
}) {
  const [draft, setDraft] = useState("");
  const { busy, error, submit } = useSubmit(onLeaveNote);
  return (
    <div className="wk-next-step" data-testid="work-next-step-note">
      <div className="is-art-lab">{NEXT_STEP_LABEL}</div>
      {/* A consumed note is struck through and stays: the record of what the
          step was told is worth more than a tidy box. */}
      {consumed.map((note) => (
        <p key={note.id} className="wk-next-step-done" data-testid="work-next-step-consumed">
          <s>{note.body}</s>
          <span className="wk-next-step-done-note">{NEXT_STEP_CONSUMED}</span>
        </p>
      ))}
      {standing && (
        <p className="wk-next-step-standing" data-testid="work-next-step-standing">
          {standing.body}
        </p>
      )}
      <textarea
        className="wk-gate-input"
        rows={2}
        value={draft}
        placeholder={NEXT_STEP_PLACEHOLDER}
        aria-label={NEXT_STEP_PLACEHOLDER}
        data-testid="work-next-step-input"
        onChange={(e) => setDraft(e.target.value)}
      />
      <div className="tri-actions">
        <button
          type="button"
          className="is-quiet"
          disabled={!draft.trim() || busy}
          title={NEXT_STEP_EFFECT}
          data-testid="work-next-step-save"
          onClick={() => void submit(draft, () => setDraft(""))}
        >
          {NEXT_STEP_SAVE}
        </button>
        <span className="wk-next-step-hint" data-testid="work-next-step-hint">
          {NEXT_STEP_HINT}
        </span>
      </div>
      {error && (
        <p className="wk-gate-err" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

/* ── 31. run again ─────────────────────────────────────────────────────────── */

/* Which runs carry the verb is `canRunAgain` in @yagni/shared: the same rule
   the item rail reads, so the two never offer different runs. */

export function RunAgainButton({
  runId,
  onRunAgain,
}: {
  runId: string;
  onRunAgain: (runId: string) => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <>
      <button
        type="button"
        className="wk-run-btn"
        disabled={busy}
        title={RUN_AGAIN_EFFECT}
        data-testid={`wk-run-again-${runId}`}
        onClick={() => {
          setBusy(true);
          setError(null);
          void onRunAgain(runId)
            .catch((err: unknown) =>
              setError(err instanceof Error ? err.message : "That did not start."),
            )
            .finally(() => setBusy(false));
        }}
      >
        {RUN_AGAIN_VERB}
      </button>
      {error && (
        <span className="wk-gate-err" role="alert">
          {error}
        </span>
      )}
    </>
  );
}
